import {
  META_EXPRESSION_SCOPE,
  META_EXPRESSION_TARGET_TYPE,
  parseRawMetaExpression,
} from "fieldpro-tools";
import _ from "lodash";

import { TValidateFunction } from "components/Forms/CreateEditViewForm";
import { isAlphanumericalStartingWithLetter } from "utils/utils";

import {
  CALLED_FROM,
  IMetaExpressionWizardAdditionnalProps,
  IMetaExpressionWizardAttributes,
} from "../../components/Dialog/MetaExpressionDialog/MetaExpressionWizard/MetaExpressionWizard";

export const getLinearProgress = (
  currentStep: number,
  scope?: META_EXPRESSION_SCOPE,
  target_type?: META_EXPRESSION_TARGET_TYPE,
) => {
  if (
    target_type &&
    [
      META_EXPRESSION_TARGET_TYPE.SQL,
      META_EXPRESSION_TARGET_TYPE.TEXT,
    ].includes(target_type)
  )
    return 100;
  else if (scope === META_EXPRESSION_SCOPE.CLIENT_ENV_VARIABLE) return 100;
  else if (scope && scope === META_EXPRESSION_SCOPE.WORKFLOW_REPORT) {
    const maxStepNb = 4;
    return 100 * (currentStep / maxStepNb);
  } else {
    // if not in workflow report, we are "skipping" the 2nd step
    const maxStepNb = 3;
    return (
      100 * ((currentStep > 2 ? currentStep - 1 : currentStep) / maxStepNb)
    );
  }
};

export const getErrorMessagesForMetaExpression: TValidateFunction<
  IMetaExpressionWizardAttributes,
  IMetaExpressionWizardAdditionnalProps
> = ({ attributes, lang }) => {
  const {
    title = "",
    scope,
    field,
    custom_field1,
    step,
    target_type,
    target_content,
  } = attributes;
  const result: any = {};
  const isTextLikeTarget =
    target_type &&
    [
      META_EXPRESSION_TARGET_TYPE.SQL,
      META_EXPRESSION_TARGET_TYPE.TEXT,
    ].includes(target_type);
  if (_.isEmpty(title)) {
    result["title"] = lang.components.inputErrors.empty;
  }

  if (_.isEmpty(scope) && !isTextLikeTarget) {
    result["scope"] = lang.components.inputErrors.empty;
  }

  if (scope && !(scope in META_EXPRESSION_SCOPE) && !isTextLikeTarget) {
    result["scope"] = lang.components.inputErrors.empty;
  }

  if (
    _.isEmpty(target_content) &&
    target_type &&
    [
      META_EXPRESSION_TARGET_TYPE.SQL,
      META_EXPRESSION_TARGET_TYPE.TEXT,
    ].includes(target_type)
  ) {
    result["target_content"] = lang.components.inputErrors.empty;
  }

  const isEnvVariable = scope === META_EXPRESSION_SCOPE.CLIENT_ENV_VARIABLE;

  if (isEnvVariable) {
    if (!field || field.length === 0) {
      result["field"] = lang.components.inputErrors.empty;
    }
  } else {
    if (_.isEmpty(field) && _.isEmpty(custom_field1) && !isTextLikeTarget) {
      result["field"] = lang.components.inputErrors.empty;
      result["custom_field1"] = lang.components.inputErrors.empty;
    }

    if ((!field || field.length === 0) && !custom_field1 && !isTextLikeTarget) {
      result["field"] = lang.components.inputErrors.empty;
    }
  }

  if (!isAlphanumericalStartingWithLetter(title)) {
    if (title.length === 0) result["title"] = lang.components.inputErrors.empty;
    else
      result["title"] = lang.components.inputErrors.alphanumericMetaExpression;
  }

  if (scope === META_EXPRESSION_SCOPE.WORKFLOW_REPORT && !step) {
    result["step"] = lang.components.inputErrors.empty;
  }
  return result;
};

const DEFAULT_REPORT_FIELDS_BY_STEP: Record<number, string[]> = {
  1: ["title", "scope"],
  2: ["field", "custom_field1", "option_tag", "custom_field2"],
};

const WORKFLOW_REPORT_FIELDS_BY_STEP: Record<number, string[]> = {
  1: ["title", "scope"],
  2: ["step"],
  3: ["field", "custom_field1", "option_tag", "custom_field2"],
};

const CLIENT_ENV_VARIABLE_FIELDS_BY_STEP: Record<number, string[]> = {
  1: ["title", "scope", "field"],
};

const getFieldsByStep = (
  scope?: META_EXPRESSION_SCOPE,
): Record<number, string[]> => {
  if (scope === META_EXPRESSION_SCOPE.CLIENT_ENV_VARIABLE) {
    return CLIENT_ENV_VARIABLE_FIELDS_BY_STEP;
  }
  if (scope === META_EXPRESSION_SCOPE.WORKFLOW_REPORT) {
    return WORKFLOW_REPORT_FIELDS_BY_STEP;
  }
  return DEFAULT_REPORT_FIELDS_BY_STEP;
};

interface IStepHasErrors {
  scope?: META_EXPRESSION_SCOPE;
  currentStep: number;
  errors: {
    [key: string]: string;
  };
}

export const stepHasErrors = ({
  scope,
  currentStep,
  errors,
}: IStepHasErrors) => {
  const FIELDS_BY_STEP = getFieldsByStep(scope);
  const stepKeys = FIELDS_BY_STEP[currentStep];
  const stepErrors = _.pick(errors, stepKeys);
  return !_.isEmpty(stepErrors);
};

interface IGetAllowedMetaExpressionScopes {
  calledFrom?: CALLED_FROM;
  includeCustomerScope?: boolean;
  includeIntegRecordScope?: boolean;
  includeTriggerEventScope?: boolean;
}

/**
 * Returns the allowed META_EXPRESSION_SCOPEs for a given UI context.
 *
 * Design choices:
 * - calledFrom is the primary axis: each value maps to a fixed, explicit scope list.
 * - Boolean flags (includeCustomerScope, includeIntegRecordScope) are used only for
 *   runtime conditions that can't be known statically (e.g. whether the current activity
 *   uses a customer list). They must not create implicit sub-modes — if a context
 *   genuinely needs a different set of scopes, add a new CALLED_FROM value instead.
 */
export const getAllowedMetaExpressionScopes = ({
  calledFrom,
  includeCustomerScope,
  includeIntegRecordScope,
  includeTriggerEventScope,
}: IGetAllowedMetaExpressionScopes = {}) => {
  switch (calledFrom) {
    case CALLED_FROM.ACTIVITY: {
      return [
        META_EXPRESSION_SCOPE.ACTIVITY_REPORT,
        META_EXPRESSION_SCOPE.USER,
        META_EXPRESSION_SCOPE.CLIENT,
        ...(includeCustomerScope ? [META_EXPRESSION_SCOPE.CUSTOMER] : []),
      ];
    }
    // Special case for item restrictions on SCOL, MCOL, and MXOL questions.
    // When the question is bound to list A, the condition must include at least
    // one operand on the "active item" / option (hence the ITEM scope)
    // so we can decide whether it passes the "restriction" filter.
    // NOTE: LIST_ITEMS_RESTRICTIONS would behave the same as LIST, so we omit it.
    // WARNING: MXOL restrictions also use ITEM, never ITEM_CELL: the mobile filters
    // the list items before any matrix row exists (FP-13942).
    case CALLED_FROM.ACTIVITY_ITEMS_RESTRICTIONS: {
      return [
        META_EXPRESSION_SCOPE.ITEM,
        META_EXPRESSION_SCOPE.ACTIVITY_REPORT,
        META_EXPRESSION_SCOPE.USER,
        META_EXPRESSION_SCOPE.CLIENT,
        ...(includeCustomerScope ? [META_EXPRESSION_SCOPE.CUSTOMER] : []),
      ];
    }
    // NOTE: we excluded USER and CLIENT scopes from OPERATION call sites, assuming there are no relevant number values to point to in those objects
    // Further details: https://optimetriks.atlassian.net/browse/FP-7696?focusedCommentId=79939
    case CALLED_FROM.ACTIVITY_OPERATION: {
      return [
        META_EXPRESSION_SCOPE.ACTIVITY_REPORT,
        ...(includeCustomerScope ? [META_EXPRESSION_SCOPE.CUSTOMER] : []),
      ];
    }
    // NOTE: same comment as for ACTIVITY_OPERATION
    case CALLED_FROM.ACTIVITY_OPERATION_MATRIX_CELL: {
      return [
        META_EXPRESSION_SCOPE.ITEM_CELL,
        META_EXPRESSION_SCOPE.MATRIX_CELL,
        META_EXPRESSION_SCOPE.ACTIVITY_REPORT,
        ...(includeCustomerScope ? [META_EXPRESSION_SCOPE.CUSTOMER] : []),
      ];
    }
    case CALLED_FROM.LIST: {
      return [
        META_EXPRESSION_SCOPE.ITEM,
        META_EXPRESSION_SCOPE.USER,
        META_EXPRESSION_SCOPE.CLIENT,
      ];
    }
    case CALLED_FROM.LIST_ITEM_NAME_TEMPLATE: {
      return [META_EXPRESSION_SCOPE.ITEM];
    }
    case CALLED_FROM.WORKFLOW_SETTINGS:
    case CALLED_FROM.WORKFLOW: {
      return [
        META_EXPRESSION_SCOPE.WORKFLOW_REPORT,
        // META_EXPRESSION_SCOPE.JOB_REPORT, // TODO: allow this too ? covered by workflow_report > step_id ?
        META_EXPRESSION_SCOPE.USER,
        META_EXPRESSION_SCOPE.CLIENT,
        META_EXPRESSION_SCOPE.CLIENT_ENV_VARIABLE,

        // Example, see https://optimetriks.atlassian.net/browse/FP-4920
        ...(calledFrom === CALLED_FROM.WORKFLOW_SETTINGS || includeCustomerScope
          ? [META_EXPRESSION_SCOPE.CUSTOMER]
          : []),

        ...(includeIntegRecordScope
          ? [META_EXPRESSION_SCOPE.INTEG_RECORD]
          : []),
        ...(includeTriggerEventScope
          ? [META_EXPRESSION_SCOPE.TRIGGER_EVENT]
          : []),
      ];
    }
    case CALLED_FROM.CUSTOMER_TO_USE: {
      return [META_EXPRESSION_SCOPE.WORKFLOW_REPORT];
    }
    case CALLED_FROM.RECEIPT_HEADER: {
      return [
        META_EXPRESSION_SCOPE.USER,
        META_EXPRESSION_SCOPE.CUSTOMER,
        META_EXPRESSION_SCOPE.CLIENT,
      ];
    }
    case CALLED_FROM.CUSTOMER_PROMO_GROUP_CONDITION: {
      return [META_EXPRESSION_SCOPE.CUSTOMER];
    }
    case CALLED_FROM.MATRIX_CELL: {
      return [
        META_EXPRESSION_SCOPE.USER,
        META_EXPRESSION_SCOPE.CLIENT,
        META_EXPRESSION_SCOPE.ITEM_CELL,
        META_EXPRESSION_SCOPE.MATRIX_CELL,
        META_EXPRESSION_SCOPE.ACTIVITY_REPORT,
        ...(includeCustomerScope ? [META_EXPRESSION_SCOPE.CUSTOMER] : []),
      ];
    }
    case CALLED_FROM.JOB_NOTIFICATION: {
      return [META_EXPRESSION_SCOPE.CLIENT, META_EXPRESSION_SCOPE.USER];
    }
    case CALLED_FROM.JOB_TRANSFORMATION: {
      return [
        META_EXPRESSION_SCOPE.CLIENT,
        META_EXPRESSION_SCOPE.WORKFLOW_REPORT,
        // META_EXPRESSION_SCOPE.JOB_REPORT,
        // META_EXPRESSION_SCOPE.TRANSFORMATION,
      ];
    }
    case CALLED_FROM.TRIGGER: {
      return [
        META_EXPRESSION_SCOPE.CLIENT,
        META_EXPRESSION_SCOPE.USER,
        META_EXPRESSION_SCOPE.TRIGGER_EVENT,
        META_EXPRESSION_SCOPE.CLIENT_ENV_VARIABLE,
      ];
    }
    default: {
      return [];
    }
  }
};

export const hasMetaExpressionString = (str?: string) => {
  if (!str) {
    return false;
  }
  const trimmed = str?.trim() ?? "";
  return (
    (trimmed.startsWith("${") || trimmed.startsWith("$^")) &&
    (trimmed.endsWith("}") || trimmed.endsWith("^"))
  );
};

export const getMetaExpressionString = (str?: string) => {
  if (!str || !hasMetaExpressionString(str)) {
    return "";
  }
  const safeStr = str?.trim() ?? "";

  // Replace "${" at the beginning
  let result = safeStr.startsWith("${") ? safeStr.replace("${", "") : safeStr;

  // Replace "$^" at the beginning
  result = result.startsWith("$^") ? result.replace("$^", "") : result;

  // Replace "}" at the end
  result = result.endsWith("}") ? result.slice(0, -1) : result;

  // Replace "^" at the end
  result = result.endsWith("^") ? result.slice(0, -1) : result;

  return result;
};

export const getFormattedExpressionFromString = (str?: string) => {
  const metaExpressionString = getMetaExpressionString(str);
  const formattedExpression = parseRawMetaExpression(metaExpressionString);
  return formattedExpression;
};

interface IGetMetaExpressionDisplayTitle {
  expression: string;
  title?: string;
}

export const getMetaExpressionDisplayTitle = ({
  expression,
  title,
}: IGetMetaExpressionDisplayTitle): string => {
  if (title && title?.length > 0) {
    return title;
  }
  const expressionPart = expression?.includes("/")
    ? (expression?.split("/")[1] ?? expression)
    : expression;

  const titleFromExpression = expressionPart
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

  if (!titleFromExpression) {
    return "";
  }

  const displayTitle = /^[a-z]/.test(titleFromExpression)
    ? titleFromExpression
    : `m_${titleFromExpression}`;
  return isAlphanumericalStartingWithLetter(displayTitle) ? displayTitle : "";
};
